import { ClickAwayListener, Grid, Stack } from '@mui/material';
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, VariableSizeList } from 'react-window';
import useBreakpoints from '../../../../hooks/useBreakpoint';
import {
    IEplassVulnerabilityData,
    VulnerabilityDisplayType,
    VulnerabilityViewType,
} from '../../../../redux/vulnerabilities/sliceVulnerabilities';
import { IAppReduxStore } from '../../../../redux/store';
import { defaultBorder } from '../../../../Theme';
import { sortData } from '../../../General/DynamicTree/DynamicTreeUtils';
import ResizeableDrawer from '../../../General/ResizeableDrawer/ResizeableDrawer';
import VulnerabilityStatusSidebar from './VulnerabilityViewSidebar';
import VulnerabilityTile from './VulnerabilityTile';
import { buildVulnerabilityGroups, filterVulnerabilities } from './VulnerabilityUtils';
import VulnerabilitiesInfo from '../../../General/Info/InfoDialog/VulnerabilitiesInfo';

const C_RowHeight = 32;
const C_TileHeight = 260;

/**
 * Copnditionally returns a value based on the truthyness of a given statement.
 * @param condition - Statement to be evaluated as a boolean.
 * @param valueIfTrue - Value to return if the condition is true.
 * @param valueIfFalse - Value to return if the condition is false.
 */
const getValue = <A, B, C>(condition: A, valueIfTrue: B, valueIfFalse: C): B | C =>
    condition ? valueIfTrue : valueIfFalse;

let vProps: string[] = [];

/**
 * Component that shows the user's vulnerabilities.
 */
export default () => {
    /** Determine screen size. */
    const { isDesktop } = useBreakpoints();

    /** Get relevant values from Redux store. */
    const displayType = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.displayType,
    );
    const activeCategory = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.activeCategory,
    );
    const sortAz = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.sortAz,
    );
    const filter = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.filter,
    );
    const vulnerabilities = useSelector((store: IAppReduxStore) => store.vulnerabilities.data);
    const viewType = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.viewType,
    );
    const sidebarOpen = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.sidebarOpen,
    );
    useEffect(() => {
        if (vulnerabilities?.length) {
            vProps = Object.keys(vulnerabilities[0]);
        }
    }, [vulnerabilities]);

    /** State slice that stores the number of vulnerability tiles that can be shown next to one another in the viewport. */
    const [colCount, setColCount] = useState<number>(1);

    /** Ref for syncing scroll position. */
    const listRef = useRef<HTMLElement>();

    /** No vulnerabilities provided - show nothing. */
    if (!vulnerabilities?.length) {
        return <VulnerabilitiesInfo />;
    }

    /**
     * Callback that allows the react-window list to be re-rendered when it is resized.
     */
    const onListSizeChange = useCallback(
        (ref: React.MutableRefObject<FixedSizeList>, width: number) => {
            if (ref) {
                ref.current?.resetAfterIndex(0);

                setColCount(Math.max(Math.floor(width / 400), 1));
            }
        },
        [setColCount],
    );

    /** Whether to show vulnerability tiles or not. */
    const isTileView = displayType === VulnerabilityDisplayType.Tile;

    const categorisedVulnerabilities = (): IEplassVulnerabilityData[] => {
        const currentViewSelection: string = Object.values(VulnerabilityViewType)
            [viewType].toString()
            .toLowerCase();

        if (activeCategory == 'All') {
            return vulnerabilities;
        }

        return vulnerabilities.filter(object => object[currentViewSelection] == activeCategory);
    };

    /** Data used for rendering the react-window list. */
    const listData = processListData({
        vulnerabilities: categorisedVulnerabilities(),
        filter,
        viewType,
        sortAz,
        colCount,
        isTileView,
    });

    /**
     * Creates the props needed to render a given vulnerability in the view.
     * @param vulnerability - Target vulnerability.
     * @returns Props object for VulnerabilityRowTile or VulnerabilityRow.
     */
    const rowProps = (vulnerability: IEplassVulnerabilityData) => ({
        key: vulnerability.ticketId,
        vulnerability: vulnerability,
        size: colCount,
    });

    /**
     * Renders a row of data in the react-window list.
     */
    const renderRow = ({ index, style }: { index: number; style: CSSProperties }) => {
        const item: string | IEplassVulnerabilityData | IEplassVulnerabilityData[] =
            listData[index];

        /** This is a header, to return a h6 Typography element. */
        if (typeof item === 'string') return <></>;

        /** If there are multiple items in the data-row, then create a MUI Grid to display them. */
        if (Array.isArray(item)) {
            return (
                <Grid container sx={{ ...style, m: 0, px: 4 }} spacing={1}>
                    {item.map(x => (
                        <VulnerabilityTile {...rowProps(x)} />
                    ))}
                </Grid>
            );
        }

        return (
            <>
                <VulnerabilityTile
                    {...rowProps(item)}
                    style={{ ...style }}
                />
            </>
        );
    };

    /**
     * Creates the react-window list to be rendered.
     * @param width - Element width.
     * @param height - Element height.
     */
    const getList = (width: number, height: number) => {
        /**
         * Gets the row height.
         * @param index - Row index.
         * @returns Row height.
         */
        const getItemHeight = (index: number) =>
            getValue(typeof listData[index] === 'string', C_RowHeight, C_TileHeight);

        /**
         * Passes a ref to the list which syncs scroll position and re-renders the list when width changes.
         * @param target - Target JSX Element.
         */
        const passRef = (target: HTMLElement) => {
            /** Update listRef so that scroll position can be synced. */
            listRef.current = target;

            /** Only need to trigger onListSizeChange if displayType is 'Tiles'. */
            isTileView && onListSizeChange(listRef, width);
        };

        /** Base props that are passed to VariableSizeList or FixedSizeList. */
        const listProps = {
            /** Pass element dimensions from AutoSizer. */
            height: height,
            width: width,
            /** Tell react-window how many rows it will need to render. */
            itemCount: listData.length,
            /** Pass the ref function to the  */
            ref: passRef,
            /** Item size is dynamic if displayType is 'Tiles'; otherwise it is static */
            itemSize: getValue(isTileView, getItemHeight, C_RowHeight),
            /** Children is the function responsible for rendering rows in the List. */
            children: renderRow,
        };

        /** Wrap list in an element to have the corrent outer spacing. */
        return (
            <>
                {getValue(
                    isTileView,
                    <VariableSizeList {...listProps} />,
                    <FixedSizeList {...listProps} />,
                )}
            </>
        );
    };

    /** AutoSizer component that contains the vulnerability list. */
    const main = <AutoSizer>{({ width, height }) => getList(width, height)}</AutoSizer>;

    /** Mobile/Tablet view. */
    if (!isDesktop) {
        return !sidebarOpen ? (
            <Stack direction='column' sx={{ width: '100%' }}>
                {main}
            </Stack>
        ) : (
            <></>
        );
    }

    /** Desktop view. */
    return (
            <Stack direction='row' sx={{ width: '100%', overflow: 'hidden' }}>
                {sidebarOpen ? (
                    <ResizeableDrawer
                        minWidth={250}
                        maxWidth={1000}
                        children={<VulnerabilityStatusSidebar />}
                    />
                ) : (
                    <Stack
                        sx={{ width: 56, position: 'relative', borderRight: defaultBorder }}
                        children={<VulnerabilityStatusSidebar />}
                    />
                )}

                <Stack flexGrow={4}>{main}</Stack>
            </Stack>
    );
};

/**
 * Processes a collection of user vulnerabilities so that they can be shown in the vulnerabilities View.
 * @param vulnerabilitiesGroups - vulnerabilities groups to process.
 * @param opts - Options object.
 */
const processListData = (opts: IProcessListDataOpts) => {
    /** Deconstruct opts. */
    const { activeCategory, colCount, filter, isTileView, vulnerabilities, sortAz } = opts;

    /** Create groups of the user's filtered vulnerabilities. */
    const vulnerabilityGroups = buildVulnerabilityGroups(
        filterVulnerabilities(vulnerabilities, filter, vProps),
    );

    /** Output data array. */
    const out: Array<string | IEplassVulnerabilityData | IEplassVulnerabilityData[]> = [];

    /** Create a list of vulnerability group names that start with activeCategory (if set). */
    sortData<IEplassVulnerabilityData, string>(vulnerabilityGroups, 'ticketId', sortAz)
        .filter(
            x =>
                !activeCategory ||
                new RegExp(`^${activeCategory.replace(/\\/g, '\\\\')}(\\\\|$)`, 'i').test(x),
        )
        /** Process the group data to create renderable rows. */
        .forEach(group => {
            out.push(group);

            let arr = [];

            sortData<IEplassVulnerabilityData, IEplassVulnerabilityData>(
                vulnerabilityGroups[group],
                'ticketId',
                sortAz,
            ).forEach(vulnerability => {
                if (!isTileView || colCount === 1) {
                    out.push(vulnerability);

                    return;
                }

                arr.push(vulnerability);

                if (arr.length === colCount) {
                    out.push([...arr]);
                    arr = [];
                }
            });

            if (arr.length) {
                out.push(arr);
            }
        });

    return out;
};

/**
 * Interface that describes the configuration options for processListData.
 */
interface IProcessListDataOpts {
    /** Whether to sort the vulnerability data alphabetically. */
    sortAz: boolean;

    /** Number of items to show next to one another. */
    colCount: number;

    /** Whether the display type is set to tile view or not. */
    isTileView: boolean;

    /** The user's selected category. */
    activeCategory?: string;

    /** Array of user vulnerabilities. */
    vulnerabilities: IEplassVulnerabilityData[];

    /** User-defined filter string. */
    filter: string;

    /** Active vulnerability view type. */
    viewType: VulnerabilityViewType;
}
